import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Wine, Utensils } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import TakeawayMenuDialog from "@/components/TakeawayMenuDialog";
import BarMenuDialog from "@/components/BarMenuDialog";
import DineInMenuDialog from "@/components/DineInMenuDialog";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useLanguage } from "@/contexts/LanguageContext";
import { use3DTilt } from "@/hooks/use3DTilt";

const Menu = () => {
  const { t } = useLanguage();
  const { ref: sectionRef, isVisible } = useScrollAnimation(0.2);
  const [showTakeawayMenu, setShowTakeawayMenu] = useState(false);
  const [showBarMenu, setShowBarMenu] = useState(false);
  const [showDineInMenu, setShowDineInMenu] = useState(false);
  
  // 3D tilt refs for each card
  const tiltRef1 = use3DTilt(8);
  const tiltRef2 = use3DTilt(8);
  const tiltRef3 = use3DTilt(8);
  
  const menus = [
    {
      icon: <Utensils className="w-12 h-12" />,
      title: t('menu.takeaway'),
      description: t('menu.takeawayDesc'),
      color: "from-gold/20 to-gold-light/20",
      buttonText: t('menu.seeTakeaway'),
      pdfLink: "/menus/takeaway-menu.pdf"
    },
    {
      icon: <Wine className="w-12 h-12" />,
      title: t('menu.bar'),
      description: t('menu.barDesc'),
      color: "from-burgundy/20 to-secondary/20",
      buttonText: t('menu.seeBar'),
      pdfLink: "/menus/bar-menu.pdf"
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: t('menu.alacarte'),
      description: t('menu.alacarteDesc'),
      color: "from-gold-light/20 to-gold/20",
      buttonText: t('menu.seeAlacarte'),
      pdfLink: "/menus/dine-in-menu.pdf"
    }
  ];

  const handleMenuClick = (pdfLink: string, title: string) => {
    // Öppna takeaway-menyn i dialog istället för PDF
    if (title === t('menu.takeaway')) {
      setShowTakeawayMenu(true);
      return;
    }
    
    // Öppna bar-menyn i dialog istället för PDF
    if (title === t('menu.bar')) {
      setShowBarMenu(true);
      return;
    }
    
    // Öppna dine-in menyn i dialog istället för PDF
    if (title === t('menu.alacarte')) {
      setShowDineInMenu(true);
      return;
    }
    
    try {
      window.open(pdfLink, '_blank', 'noopener,noreferrer');
      toast({
        title: "Öppnar meny",
        description: `${title} öppnas i en ny flik`,
      });
    } catch (error) {
      toast({
        title: "Kunde inte öppna menyn",
        description: "Försök ladda ner den istället",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <TakeawayMenuDialog 
        open={showTakeawayMenu} 
        onOpenChange={setShowTakeawayMenu} 
      />
      <BarMenuDialog 
        open={showBarMenu} 
        onOpenChange={setShowBarMenu} 
      />
      <DineInMenuDialog 
        open={showDineInMenu} 
        onOpenChange={setShowDineInMenu} 
      />
      
      <section id="menu" className="section-padding bg-background" ref={sectionRef}>
        <div className="container-custom">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-gold text-lg font-medium tracking-wider uppercase mb-4 animate-text-glow">{t('menu.title')}</p>
            <h2 className="heading-luxury text-foreground mb-6 text-gradient">
              {t('menu.heading')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('menu.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {menus.map((menu, index) => {
              const tiltRefs = [tiltRef1, tiltRef2, tiltRef3];
              return (
                <Card
                  key={index}
                  ref={tiltRefs[index]}
                  className={`overflow-hidden group hover:shadow-[0_20px_60px_rgba(212,175,55,0.3)] border-border bg-gradient-to-br ${menu.color} transition-all duration-500 ${
                    isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                  }`}
                  style={{ 
                    transitionDelay: `${index * 0.15}s`,
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out'
                  }}
                >
                  <CardContent className="p-6 sm:p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-background text-gold mb-4 sm:mb-6 shadow-gold transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {menu.icon}
                    </div>
                  <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground mb-2 sm:mb-3">
                    {menu.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                    {menu.description}
                  </p>
                  <Button
                    variant="outline"
                    className="border-gold text-gold hover:bg-gold hover:text-background w-full transform hover:scale-105 transition-all"
                    onClick={() => handleMenuClick(menu.pdfLink, menu.title)}
                  >
                    {menu.buttonText}
                  </Button>
                </CardContent>
              </Card>
              );
            })}
          </div>

          <div className={`mt-16 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.5s' }}>
            <p className="text-muted-foreground mb-6">
              {t('menu.allergies')}
            </p>
            <Button
              size="lg"
              className="bg-gold hover:bg-gold-light text-background font-semibold transform hover:scale-105 transition-all"
              asChild
            >
              <a href="#contact">{t('menu.contact')}</a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Menu;
